import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import type { ReactElement } from 'react'
import {
  buttonTab,
  buttonTabActive,
  buttonTabError,
  buttonTabGroup,
  buttonTabIcon,
  buttonTabLabel,
  buttonTabSuccess,
  buttonTabWarning
} from './ButtonTabGroup.css'

export type ButtonTabVariant = 'success' | 'warning' | 'error'

export type ButtonTabOption<T extends string> = {
  label: string
  value: T
  icon?: IconDefinition
  variant?: ButtonTabVariant
}

type ButtonTabGroupProps<T extends string> = {
  options: ButtonTabOption<T>[]
  value: T | null
  onChange: (value: T) => void
  className?: string
}

const variantClass: Record<ButtonTabVariant, string> = {
  success: buttonTabSuccess,
  warning: buttonTabWarning,
  error: buttonTabError
}

export const ButtonTabGroup = <T extends string>({
  options,
  value,
  onChange,
  className
}: ButtonTabGroupProps<T>): ReactElement => (
  <div className={clsx(buttonTabGroup, className)}>
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        onClick={() => onChange(option.value)}
        className={clsx(
          buttonTab,
          option.variant && variantClass[option.variant],
          value === option.value && buttonTabActive
        )}
      >
        {option.icon && (
          <span className={buttonTabIcon}>
            <FontAwesomeIcon icon={option.icon} />
          </span>
        )}
        <span className={buttonTabLabel}>{option.label}</span>
      </button>
    ))}
  </div>
)
